// src/contexts/UserContext.tsx
import React, { createContext, useState, useContext, ReactNode, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { formatSchoolId } from "../utils/formatUtils";

export interface UserProfile {
  _id: string;
  id: string;
  userId?: string;     // Make userId optional
  email: string;
  name?: string;
  firstName?: string;  // Add firstName
  lastName?: string;   // Add lastName
  avatarUrl?: string;
  schoolId: string;        // Make schoolId required
  school_id?: string;  // Add this to handle backend format
  school?: string;     // Add this for fallback
  grade?: string;           
  session?: string;         
  strictModeActivated?: boolean;  
  role?: string;       // Add this for user role
  onboardingCompleted?: boolean;
  teacherId?: string;  // MongoDB _id of the teacher document
  profilePhoto?: string;  // Add profilePhoto property
  // Add more fields as needed
}

interface UserContextType {
  profile: UserProfile | null;
  setProfile: (profile: UserProfile | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const { user } = useAuth();

  // Sync with AuthContext user data
  useEffect(() => {
    if (user) {
      const formattedSchoolId = formatSchoolId(user.school || '', user.id);
      const updatedProfile = {
        _id: user.id,
        id: user.id,
        userId: user.id,
        email: user.email,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        firstName: user.firstName,
        lastName: user.lastName,
        schoolId: formattedSchoolId,
        school: user.school,
        grade: user.grade,
        session: user.session,
        role: user.role,
        onboardingCompleted: user.onboardingCompleted,
        strictModeActivated: user.strictModeActivated,
        teacherId: user.teacherId // Add teacherId from user context
      };
      setProfile(updatedProfile);
    } else {
      setProfile(null);
    }
  }, [user]);

  //update the profile with the latest school ID
  const updateProfile = (newProfile: UserProfile | null) => {
    if (newProfile) {
      // Format school ID consistently
      const formattedSchoolId = formatSchoolId(newProfile.school || '', newProfile.userId || newProfile.id);
      const updatedProfile = {
        ...newProfile,
        userId: newProfile.userId || newProfile.id, // Use id as fallback for userId
        schoolId: formattedSchoolId, // Always use formatted school ID
        grade: newProfile.grade || 'UNKNOWN',
        role: newProfile.role || 'Student',
        onboardingCompleted: newProfile.onboardingCompleted || false,
        // Preserve firstName and lastName if they exist, otherwise derive from name
        firstName: newProfile.firstName || (newProfile.name ? newProfile.name.split(' ')[0] : ''),
        lastName: newProfile.lastName || (newProfile.name ? newProfile.name.split(' ').slice(1).join(' ') : ''),
        // Ensure name is set if not provided
        name: newProfile.name || `${newProfile.firstName || ''} ${newProfile.lastName || ''}`.trim(),
        teacherId: newProfile.teacherId // Preserve teacherId
      };
      setProfile(updatedProfile);
    } else {
      setProfile(null);
    }
  };

  return (
    <UserContext.Provider value={{ profile, setProfile: updateProfile }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export {};
